import styled, { keyframes } from 'styled-components'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import colors from '../utils/style/colors'

const bgShift = keyframes`from{filter:brightness(1)}to{filter:brightness(1.1)saturate(1.1)}`
const fadeUp  = keyframes`from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}`

// ─── MINI HERO ───
const MiniHero = styled.section`
  position:relative;height:260px;display:flex;align-items:center;justify-content:center;
  overflow:hidden;background:#0e1a0a;
  @media(max-width:768px){height:200px}
`
const HeroBg = styled.div`
  position:absolute;inset:0;
  background:
    radial-gradient(ellipse 80% 60% at 20% 60%,rgba(58,90,46,.5) 0%,transparent 65%),
    radial-gradient(ellipse 70% 50% at 80% 40%,rgba(107,76,42,.35) 0%,transparent 60%),
    linear-gradient(160deg,#0e1a0a 0%,#1e2e14 40%,#2a1a0a 100%);
  animation:${bgShift} 10s ease-in-out infinite alternate;
`
const HeroGrain = styled.div`
  position:absolute;inset:0;pointer-events:none;opacity:.05;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size:200px
`
const HeroContent = styled.div`position:relative;z-index:2;text-align:center;animation:${fadeUp} .8s both`
const HeroBadge = styled.span`
  display:inline-block;background:rgba(200,168,75,.15);border:1px solid rgba(200,168,75,.4);
  color:#e8c96b;font-size:.7rem;font-weight:700;letter-spacing:.18em;text-transform:uppercase;
  padding:6px 16px;border-radius:2px;margin-bottom:14px;font-family:${colors.font};
`
const HeroTitle = styled.h1`
  font-family:'Playfair Display',Georgia,serif;font-size:clamp(2rem,5vw,3.2rem);
  font-weight:900;color:#fff;line-height:1.1;margin:0 0 12px;
  text-shadow:0 4px 24px rgba(0,0,0,.5);
  em{font-style:normal;color:#e8c96b}
`
const HeroSub = styled.p`
  font-family:${colors.font};font-size:.95rem;color:rgba(255,255,255,.6);font-weight:300;margin:0;
`
const HeroBar = styled.div`
  position:absolute;bottom:0;left:0;right:0;height:4px;
  background:linear-gradient(90deg,transparent,#c8a84b,transparent)
`

// ─── LAYOUT ───
const Page = styled.div`background:${colors.blanc}`

// ─── INTRO ───
const IntroSection = styled.section`
  background:${colors.beige};padding:80px 48px;
  display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:center;
  @media(max-width:900px){grid-template-columns:1fr;gap:40px;padding:60px 24px}
`
const IntroText = styled.div``
const SectionTag = styled.span`
  display:inline-block;font-family:${colors.font};font-size:.68rem;font-weight:700;
  letter-spacing:.18em;text-transform:uppercase;color:${colors.or};
  border:1px solid rgba(200,168,75,.35);padding:5px 14px;border-radius:2px;margin-bottom:20px;
`
const IntroTitle = styled.h2`
  font-family:'Playfair Display',Georgia,serif;font-size:clamp(1.8rem,3vw,2.6rem);
  font-weight:900;color:${colors.texte};line-height:1.15;margin:0 0 20px;
`
const IntroP = styled.p`
  font-family:${colors.font};font-size:.95rem;color:${colors.texteLeger};
  line-height:1.75;margin:0 0 16px;&:last-child{margin:0}
`
const IntroCard = styled.div`
  background:#fff;border-radius:12px;border:1px solid ${colors.beigeFonce};
  padding:32px;box-shadow:0 8px 32px rgba(42,32,24,.08);
`
const StatGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:24px;
`
const StatBox = styled.div`
  text-align:center;padding:20px 16px;border-radius:8px;
  background:${p=>p.$dark?colors.texte:colors.beige};
`
const StatNum = styled.div`
  font-family:'Playfair Display',Georgia,serif;font-size:2.2rem;font-weight:900;
  color:${p=>p.$dark?colors.or:colors.texte};line-height:1;margin-bottom:6px;
`
const StatLabel = styled.div`
  font-family:${colors.font};font-size:.72rem;font-weight:600;letter-spacing:.08em;
  text-transform:uppercase;color:${p=>p.$dark?'rgba(255,255,255,.5)':colors.texteLeger};
`

// ─── AVANTAGES ───
const AvantagesSection = styled.section`
  padding:90px 48px;background:${colors.blanc};
  @media(max-width:768px){padding:60px 24px}
`
const SectionHeader = styled.div`text-align:center;margin-bottom:60px`
const SectionTitle = styled.h2`
  font-family:'Playfair Display',Georgia,serif;font-size:clamp(1.8rem,3vw,2.4rem);
  font-weight:900;color:${colors.texte};margin:0 0 12px;
`
const SectionSub = styled.p`
  font-family:${colors.font};font-size:.92rem;color:${colors.texteLeger};
  max-width:520px;margin:0 auto;line-height:1.65;
`
const CardsGrid = styled.div`
  display:grid;grid-template-columns:repeat(2,1fr);gap:24px;max-width:900px;margin:0 auto;
  @media(max-width:700px){grid-template-columns:1fr}
`
const AdvCard = styled.div`
  background:#fff;border:1px solid ${colors.beigeFonce};border-radius:10px;
  padding:32px 28px;display:flex;gap:20px;align-items:flex-start;
  opacity:0;transform:translateY(20px);
  transition:opacity .5s ${p=>p.$delay||'0s'},transform .5s ${p=>p.$delay||'0s'},box-shadow .3s,border-color .3s;
  &.visible{opacity:1;transform:translateY(0)}
  &:hover{box-shadow:0 12px 40px rgba(58,90,46,.1);border-color:${colors.or}}
`
const AdvIconBox = styled.div`
  width:52px;height:52px;min-width:52px;border-radius:10px;
  background:${p=>p.$gold?'rgba(200,168,75,.12)':'rgba(58,90,46,.08)'};
  display:flex;align-items:center;justify-content:center;font-size:1.5rem;
`
const AdvTitle = styled.h3`
  font-family:'Playfair Display',Georgia,serif;font-size:1.05rem;font-weight:700;
  color:${colors.texte};margin:0 0 8px;
`
const AdvDesc = styled.p`
  font-family:${colors.font};font-size:.84rem;color:${colors.texteLeger};
  line-height:1.65;margin:0;
`

// ─── ENGAGEMENTS ───
const EngagementsSection = styled.section`
  background:${colors.texte};padding:90px 48px;
  @media(max-width:768px){padding:60px 24px}
`
const EngRow = styled.div`
  max-width:820px;margin:0 auto;display:flex;flex-direction:column;gap:0;
`
const EngItem = styled.div`
  display:flex;gap:28px;align-items:flex-start;padding:28px 0;
  border-bottom:1px solid rgba(255,255,255,.07);
  &:last-child{border-bottom:none}
  opacity:0;transform:translateX(-20px);
  transition:opacity .5s ${p=>p.$delay||'0s'},transform .5s ${p=>p.$delay||'0s'};
  &.visible{opacity:1;transform:translateX(0)}
  @media(max-width:600px){gap:16px}
`
const EngNum = styled.div`
  font-family:'Playfair Display',Georgia,serif;font-size:2.8rem;font-weight:900;
  color:rgba(200,168,75,.2);line-height:1;min-width:52px;
`
const EngContent = styled.div``
const EngTitle = styled.h3`
  font-family:'Playfair Display',Georgia,serif;font-size:1.15rem;font-weight:700;
  color:#fff;margin:0 0 8px;
`
const EngDesc = styled.p`
  font-family:${colors.font};font-size:.87rem;color:rgba(255,255,255,.5);
  line-height:1.7;margin:0;
`

// ─── ZONES ───
const ZonesSection = styled.section`
  background:${colors.beige};padding:80px 48px;
  @media(max-width:768px){padding:60px 24px}
`
const ZonesGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:60px;max-width:900px;margin:0 auto;align-items:start;
  @media(max-width:768px){grid-template-columns:1fr;gap:36px}
`
const ZoneList = styled.div`display:flex;flex-direction:column;gap:10px;margin-top:24px`
const ZoneItem = styled.div`
  display:flex;align-items:center;gap:12px;padding:14px 18px;
  background:#fff;border-radius:8px;border-left:3px solid ${p=>p.$gold?colors.or:colors.vertOlive};
  font-family:${colors.font};font-size:.88rem;color:${colors.texte};font-weight:500;
`
const RouteVisual = styled.div`
  background:${colors.texte};border-radius:12px;padding:32px 28px;
  display:flex;flex-direction:column;gap:20px;
`
const RouteRow = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:12px;
`
const CountryBox = styled.div`text-align:center`
const CountryFlag = styled.div`font-size:2.2rem;margin-bottom:6px`
const CountryName = styled.div`font-family:'Playfair Display',Georgia,serif;font-size:1rem;font-weight:700;color:#fff`
const CountryRole = styled.div`font-family:${colors.font};font-size:.7rem;color:rgba(255,255,255,.4);letter-spacing:.08em;text-transform:uppercase;margin-top:2px`
const RouteArrow = styled.div`flex:1;display:flex;flex-direction:column;align-items:center;gap:4px`
const RouteLine = styled.div`width:100%;height:2px;background:linear-gradient(90deg,rgba(200,168,75,.3),${colors.or},rgba(200,168,75,.3));border-radius:1px`
const TruckEmoji = styled.div`font-size:1.4rem`
const RouteLabel = styled.div`font-family:${colors.font};font-size:.62rem;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:${colors.or}`
const RouteTagline = styled.p`
  font-family:${colors.font};font-size:.82rem;color:rgba(255,255,255,.45);
  line-height:1.65;margin:0;border-top:1px solid rgba(255,255,255,.07);padding-top:20px;
`

// ─── CTA ───
const CtaSection = styled.section`
  background:${colors.blanc};padding:80px 48px;text-align:center;
  @media(max-width:768px){padding:60px 24px}
`
const CtaTitle = styled.h2`
  font-family:'Playfair Display',Georgia,serif;font-size:clamp(1.6rem,3vw,2.2rem);
  font-weight:900;color:${colors.texte};margin:0 0 14px;
`
const CtaDesc = styled.p`
  font-family:${colors.font};font-size:.92rem;color:${colors.texteLeger};
  max-width:480px;margin:0 auto 36px;line-height:1.65;
`
const CtaBtns = styled.div`display:flex;gap:16px;justify-content:center;flex-wrap:wrap`
const BtnPrimary = styled(Link)`
  font-family:${colors.font};font-size:.88rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
  text-decoration:none;background:${colors.or};color:${colors.texte};
  padding:14px 32px;border-radius:4px;transition:all .25s;
  &:hover{background:${colors.orClair};transform:translateY(-2px);box-shadow:0 6px 20px rgba(200,168,75,.4)}
`
const BtnSecondary = styled(Link)`
  font-family:${colors.font};font-size:.88rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
  text-decoration:none;background:transparent;color:${colors.texte};
  border:1.5px solid ${colors.beigeFonce};padding:14px 32px;border-radius:4px;transition:all .25s;
  &:hover{border-color:${colors.texte};transform:translateY(-2px)}
`

export default function PourquoiNous() {
  const cardsRef = useRef(null)
  const engRef   = useRef(null)

  useEffect(() => {
    const observe = (ref, selector) => {
      if (!ref.current) return
      const els = ref.current.querySelectorAll(selector)
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target) } })
      }, { threshold: 0.15 })
      els.forEach(el => io.observe(el))
      return () => io.disconnect()
    }
    observe(cardsRef, '[data-card]')
    observe(engRef,   '[data-eng]')
  }, [])

  return (
    <Page>
      {/* HERO */}
      <MiniHero>
        <HeroBg/><HeroGrain/>
        <HeroContent>
          <HeroBadge>Notre histoire</HeroBadge>
          <HeroTitle>Pourquoi nous <em>choisir ?</em></HeroTitle>
          <HeroSub>Export direct France → Suisse, sans intermédiaire</HeroSub>
        </HeroContent>
        <HeroBar/>
      </MiniHero>

      {/* INTRO */}
      <IntroSection>
        <IntroText>
          <SectionTag>Qui sommes-nous</SectionTag>
          <IntroTitle>Un spécialiste de l'export paille & fourrage</IntroTitle>
          <IntroP>
            Gérant d'une entreprise française spécialisée dans l'export de paille et de fourrage vers la Suisse, je travaille en direct avec des céréaliers de confiance, sélectionnés pour leur constance au fil des saisons.
          </IntroP>
          <IntroP>
            Pas d'intermédiaire, pas de surprise : je gère la logistique de A à Z, du chargement chez le producteur jusqu'à la livraison dans votre exploitation. Les formalités douanières sont comprises, et vous payez uniquement au déchargement, le jour de la livraison.
          </IntroP>
          <IntroP>
            Mon objectif est simple : vous fournir une marchandise de qualité, au bon prix, avec un service fiable et un seul interlocuteur disponible à chaque étape.
          </IntroP>
        </IntroText>
        <IntroCard>
          <StatGrid>
            {[
              { num: '10+', label: "Années d'expérience", dark: true },
              { num: '2 000T+', label: 'Stock minimum disponible/saison', dark: false },
              { num: '24h',  label: 'Délai de réponse',    dark: false },
              { num: '100%', label: 'Qualité garantie',      dark: true  },
            ].map(s => (
              <StatBox key={s.label} $dark={s.dark}>
                <StatNum $dark={s.dark}>{s.num}</StatNum>
                <StatLabel $dark={s.dark}>{s.label}</StatLabel>
              </StatBox>
            ))}
          </StatGrid>
        </IntroCard>
      </IntroSection>

      {/* AVANTAGES */}
      <AvantagesSection>
        <SectionHeader>
          <SectionTag>Nos atouts</SectionTag>
          <SectionTitle>Ce qui nous distingue</SectionTitle>
          <SectionSub>Une offre pensée pour les éleveurs et agriculteurs suisses qui veulent un approvisionnement simple et fiable.</SectionSub>
        </SectionHeader>
        <div ref={cardsRef}>
          <CardsGrid>
            {[
              { icon:'🏔️', title:'Livraison clé en main', desc:"Du chargement en France jusqu'à votre exploitation en Suisse, on s'occupe de tout. Douane, logistique, transport — rien à gérer de votre côté.", gold:false, delay:'0s' },
              { icon:'⚖️', title:'Poids garanti, ticket fourni', desc:'Chaque chargement est pesé sur pont bascule. Vous recevez un ticket de pesée systématiquement — pas de mauvaises surprises à la livraison.', gold:true, delay:'.1s' },
              { icon:'💶', title:'Paiement à la livraison', desc:"Pas de règlement en avance. Vous payez uniquement au déchargement, le jour J. C'est simple, transparent, et sans engagement préalable.", gold:false, delay:'.2s' },
              { icon:'📱', title:'Un seul interlocuteur', desc:"Vous avez un contact direct et disponible pour toutes vos questions, de la demande de devis jusqu'au suivi et pour l'organisation de la livraison.", gold:true, delay:'.3s' },
              { icon:'🌾', title:"Qualité constante, toute l'année", desc:'Partenariat durable avec des céréaliers français sélectionnés. La qualité est contrôlée au chargement pour chaque commande.', gold:false, delay:'.4s' },
              { icon:'⚡', title:'Devis sous 24 heures', desc:'Envoyez votre demande, recevez une offre personnalisée en moins de 24h ouvrées. Réactif, précis, sans fioritures.', gold:true, delay:'.5s' },
            ].map(a => (
              <AdvCard key={a.title} data-card $delay={a.delay}>
                <AdvIconBox $gold={a.gold}>{a.icon}</AdvIconBox>
                <div>
                  <AdvTitle>{a.title}</AdvTitle>
                  <AdvDesc>{a.desc}</AdvDesc>
                </div>
              </AdvCard>
            ))}
          </CardsGrid>
        </div>
      </AvantagesSection>

      {/* ENGAGEMENTS */}
      <EngagementsSection>
        <SectionHeader style={{marginBottom:52}}>
          <SectionTag style={{color:colors.or,borderColor:'rgba(200,168,75,.35)'}}>Notre promesse</SectionTag>
          <SectionTitle style={{color:'#fff'}}>Nos engagements</SectionTitle>
        </SectionHeader>
        <div ref={engRef}>
          <EngRow>
            {[
              { titre:'Transparence totale',       desc:"Le prix que vous recevez est le prix que vous payez. Pas de frais cachés, pas de mauvaise surprise. Tout est inclus dans le devis : marchandise, transport, douane.", delay:'0s'   },
              { titre:'Marchandise contrôlée',     desc:'Chaque lot est inspecté avant chargement. La paille est sèche, propre et conforme à ce qui a été convenu. En cas de problème, on le règle.', delay:'.12s' },
              { titre:'Réactivité à chaque étape', desc:"De la commande à la livraison, vous êtes informé. Pas besoin de relancer : on revient vers vous avant que vous ayez à demander.", delay:'.24s' },
              { titre:'Relation sur le long terme', desc:"Beaucoup de nos clients commandent depuis plusieurs années. Ce n'est pas un hasard : on préfère construire une relation durable plutôt que de maximiser une transaction.", delay:'.36s' },
            ].map((e, i) => (
              <EngItem key={e.titre} data-eng $delay={e.delay}>
                <EngNum>0{i+1}</EngNum>
                <EngContent>
                  <EngTitle>{e.titre}</EngTitle>
                  <EngDesc>{e.desc}</EngDesc>
                </EngContent>
              </EngItem>
            ))}
          </EngRow>
        </div>
      </EngagementsSection>

      {/* ZONES */}
      <ZonesSection>
        <SectionHeader style={{marginBottom:48}}>
          <SectionTag>Couverture</SectionTag>
          <SectionTitle>Zones de livraison</SectionTitle>
          <SectionSub>Nous livrons dans toute la Suisse, avec une concentration sur la Suisse romande et les cantons limitrophes.</SectionSub>
        </SectionHeader>
        <ZonesGrid>
          <div>
            <ZoneList>
              {[
                { label:'Vaud (VD)', gold:false },
                { label:'Jura (JU)', gold:false },
                { label:'Fribourg (FR)', gold:false },
                { label:'Neuchâtel (NE)', gold:false },
                { label:'Genève (GE)', gold:false },
                { label:'Berne (BE)', gold:false },
                { label:'Valais (VS)', gold:false },
                { label:'Autres cantons — sur demande', gold:false },
                ].map(z => (
                <ZoneItem key={z.label} $gold={z.gold}>
                  <span>{z.gold ? '🟡' : '🟢'}</span> {z.label}
                </ZoneItem>
              ))}
            </ZoneList>
          </div>
          <div>
            <RouteVisual>
              <RouteRow>
                <CountryBox>
                  <CountryFlag>🇫🇷</CountryFlag>
                  <CountryName>France</CountryName>
                  <CountryRole>Chargement</CountryRole>
                </CountryBox>
                <RouteArrow>
                  <TruckEmoji>🚛</TruckEmoji>
                  <RouteLine/>
                  <RouteLabel>Export direct</RouteLabel>
                </RouteArrow>
                <CountryBox>
                  <CountryFlag>🇨🇭</CountryFlag>
                  <CountryName>Suisse</CountryName>
                  <CountryRole>Livraison</CountryRole>
                </CountryBox>
              </RouteRow>
              <RouteTagline>
                Livraison dans toute la Suisse — formalités douanières incluses dans chaque devis. Vous recevez votre marchandise directement sur votre exploitation, sans avoir à vous déplacer.
              </RouteTagline>
            </RouteVisual>
          </div>
        </ZonesGrid>
      </ZonesSection>

      {/* CTA */}
      <CtaSection>
        <SectionTag>Prêt à commander ?</SectionTag>
        <CtaTitle>Obtenez votre devis<br />en quelques minutes</CtaTitle>
        <CtaDesc>Réponse personnalisée sous 24h. Sans engagement, sans avance de paiement.</CtaDesc>
        <CtaBtns>
          <BtnPrimary to="/devis">Demander un devis gratuit →</BtnPrimary>
          <BtnSecondary to="/contact">Nous contacter</BtnSecondary>
        </CtaBtns>
      </CtaSection>
    </Page>
  )
}
